import { useEffect, useRef, useState } from "react";
import {
  type ClipboardEntry,
  deleteClipboardEntry,
  getClipboardDownloadUrl,
  listClipboard,
  postClipboardImage,
  postClipboardText,
} from "../clipboard/api";

// "Paperclip" popover.  Two operations:
//   1. Paste from this device's clipboard → push a new entry up to the
//      server so other signed-in devices can grab it.
//   2. Copy a previous entry back into this device's clipboard.
//
// Reads use navigator.clipboard.read() (text + image) with a graceful
// fallback to readText() — older iOS Safari builds expose only the
// text path.  Writes use navigator.clipboard.writeText() for text and
// navigator.clipboard.write([new ClipboardItem(...)]) for images.

const SUPPORTED_IMAGE_MIMES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);

export function ClipboardPopover({
  accessToken,
  anchorRef,
  onClose,
}: {
  accessToken: string;
  /** The trigger element — we measure its bounding rect and pin the
   *  popover below it, clamped to the viewport so we never hang off
   *  the side on narrow mobile screens. */
  anchorRef: React.RefObject<HTMLElement>;
  onClose: () => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [entries, setEntries] = useState<ClipboardEntry[] | null>(null);
  const [pasting, setPasting] = useState(false);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  // Viewport-anchored position.  We compute on mount and on resize so
  // a rotation / window-zoom doesn't strand the popover.
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  useEffect(() => {
    const POPOVER_W = 320;
    const MARGIN = 8;
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewW = window.innerWidth;
      const width = Math.min(POPOVER_W, viewW - MARGIN * 2);
      // Anchor the popover's right edge to the trigger's right edge,
      // then clamp inside the viewport on both sides.
      let left = rect.right - width;
      if (left < MARGIN) left = MARGIN;
      if (left + width > viewW - MARGIN) left = viewW - MARGIN - width;
      const top = rect.bottom + 6;
      setCoords({ top, left, width });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [anchorRef]);

  // Close on outside click / Esc.  We treat the trigger itself as
  // "inside" so its own click still drives the toggle in the parent.
  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (wrapRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  const refresh = async () => {
    try {
      const list = await listClipboard(accessToken);
      setEntries(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const flashHint = (msg: string) => {
    setHint(msg);
    setTimeout(() => setHint((cur) => (cur === msg ? null : cur)), 1800);
  };

  // Read whatever the OS clipboard offers and push as one (or more)
  // entries.  iOS gates clipboard.read on a recent user activation —
  // calling this directly from the button's onClick keeps us inside
  // that window.
  const onPaste = async () => {
    setError(null);
    setPasting(true);
    try {
      let pushedSomething = false;
      // Prefer the rich API (text + images).  When it isn't available
      // (older iOS Safari), fall back to readText.
      if (
        typeof navigator.clipboard !== "undefined" &&
        typeof navigator.clipboard.read === "function"
      ) {
        try {
          const items = await navigator.clipboard.read();
          for (const it of items) {
            // Pick the best supported mime from this item.  Images
            // first (richer), then text.
            const imageMime = it.types.find((t) =>
              SUPPORTED_IMAGE_MIMES.has(t),
            );
            if (imageMime) {
              const blob = await it.getType(imageMime);
              await postClipboardImage(accessToken, blob);
              pushedSomething = true;
              continue;
            }
            // Prefer text/plain so iOS-styled HTML doesn't drag a
            // forest of inline-style markup into the stored entry.
            // Fall back to text/html stripped to plain text.
            let text: string | null = null;
            if (it.types.includes("text/plain")) {
              text = (await (await it.getType("text/plain")).text()) ?? null;
            } else if (it.types.includes("text/html")) {
              const html = await (await it.getType("text/html")).text();
              text = htmlToPlainText(html);
            }
            if (text && text.trim().length > 0) {
              await postClipboardText(accessToken, text);
              pushedSomething = true;
            }
          }
        } catch (richErr) {
          // Some browsers reject .read() but allow readText — try that.
          const text = await navigator.clipboard.readText();
          if (text.trim().length > 0) {
            await postClipboardText(accessToken, text);
            pushedSomething = true;
          } else if (!pushedSomething) {
            throw richErr;
          }
        }
      } else if (
        typeof navigator.clipboard !== "undefined" &&
        typeof navigator.clipboard.readText === "function"
      ) {
        const text = await navigator.clipboard.readText();
        if (text.trim().length > 0) {
          await postClipboardText(accessToken, text);
          pushedSomething = true;
        }
      } else {
        throw new Error(
          "Your browser doesn't expose the clipboard. Paste into the box manually.",
        );
      }
      if (!pushedSomething) {
        flashHint("Clipboard is empty.");
      } else {
        flashHint("Saved.");
        await refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPasting(false);
    }
  };

  const onCopy = async (entry: ClipboardEntry) => {
    setError(null);
    setCopyingId(entry.id);
    try {
      if (entry.kind === "text") {
        if (!navigator.clipboard?.writeText) {
          throw new Error("Clipboard write isn't available here.");
        }
        await navigator.clipboard.writeText(entry.textContent ?? "");
      } else {
        if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
          throw new Error(
            "Image copy isn't supported on this browser. Long-press the preview to save it instead.",
          );
        }
        const url = await getClipboardDownloadUrl(accessToken, entry.id);
        // Safari needs the Blob inside ClipboardItem to be set
        // synchronously from a user gesture; here we're already inside
        // a click handler so we just await the fetch and write.
        const res = await fetch(url);
        if (!res.ok) throw new Error(`fetch ${res.status}`);
        const blob = await res.blob();
        await navigator.clipboard.write([
          new ClipboardItem({ [entry.mimeType]: blob }),
        ]);
      }
      flashHint("Copied!");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCopyingId(null);
    }
  };

  const onDelete = async (entry: ClipboardEntry) => {
    if (!confirm("Remove this clipboard entry?")) return;
    try {
      await deleteClipboardEntry(accessToken, entry.id);
      setEntries((prev) => (prev ?? []).filter((e) => e.id !== entry.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div
      ref={wrapRef}
      role="dialog"
      aria-label="Clipboard"
      style={
        coords
          ? {
              position: "fixed",
              top: coords.top,
              left: coords.left,
              width: coords.width,
              // Keep within viewport vertically too — long lists scroll
              // inside the body section.
              maxHeight: `calc(100vh - ${coords.top + 16}px)`,
            }
          : { visibility: "hidden", position: "fixed" }
      }
      className="z-40 flex flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900"
    >
      <header className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Clipboard
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close clipboard"
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          ✕
        </button>
      </header>

      <div className="flex flex-col gap-2 p-3">
        <button
          type="button"
          onClick={() => void onPaste()}
          disabled={pasting}
          className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300"
        >
          {pasting ? "Pasting…" : "📋 Paste from this device"}
        </button>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Saves your device's clipboard so your other signed-in devices can
          grab it back. Text and images (PNG / JPEG / GIF / WebP).
        </p>
        {hint && <p className="text-[11px] text-emerald-700 dark:text-emerald-300">{hint}</p>}
        {error && <p className="text-[11px] text-rose-600 dark:text-rose-400">{error}</p>}
      </div>

      <div className="max-h-80 overflow-y-auto border-t border-slate-100 p-2 dark:border-slate-800">
        {entries === null ? (
          <p className="px-1 py-2 text-xs italic text-slate-400 dark:text-slate-500">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="px-1 py-2 text-xs italic text-slate-400 dark:text-slate-500">
            No entries yet.
          </p>
        ) : (
          <ul className="space-y-1">
            {entries.map((e) => (
              <EntryRow
                key={e.id}
                entry={e}
                accessToken={accessToken}
                copying={copyingId === e.id}
                onCopy={() => void onCopy(e)}
                onDelete={() => void onDelete(e)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EntryRow({
  entry,
  accessToken,
  copying,
  onCopy,
  onDelete,
}: {
  entry: ClipboardEntry;
  accessToken: string;
  copying: boolean;
  onCopy: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="group flex items-start gap-2 rounded border border-slate-200 bg-white p-2 text-xs dark:border-slate-700 dark:bg-slate-900">
      <div className="flex-1 min-w-0">
        {entry.kind === "text" ? (
          <TextPreview content={entry.textContent ?? ""} />
        ) : (
          <ImagePreview entry={entry} accessToken={accessToken} />
        )}
        <div className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">
          {new Date(entry.createdAt).toLocaleString()}
        </div>
      </div>
      <div className="flex shrink-0 flex-col gap-1">
        <button
          type="button"
          onClick={onCopy}
          disabled={copying}
          className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          {copying ? "Copying…" : "Copy"}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded px-2 py-0.5 text-[11px] text-rose-600 opacity-0 hover:bg-rose-50 group-hover:opacity-100 dark:text-rose-400 dark:hover:bg-rose-950"
          aria-label="Delete entry"
        >
          ✕
        </button>
      </div>
    </li>
  );
}

function TextPreview({ content }: { content: string }) {
  // Three-line clamp; the title attribute exposes the full text on
  // hover so the user can confirm what they're about to copy.
  return (
    <div
      title={content}
      className="line-clamp-3 whitespace-pre-wrap break-words font-mono text-[11px] text-slate-800 dark:text-slate-100"
    >
      {content}
    </div>
  );
}

// Strip an HTML fragment to plain text using the browser's own parser
// — DOMParser gives us .textContent without the script-execution
// hazards of innerHTML.  Falls back to a regex strip if DOMParser
// isn't available (it is, in every modern browser, but defensive).
function htmlToPlainText(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const text = doc.body?.textContent ?? "";
    // Collapse the runs of whitespace that <p>, <br>, etc. tend to
    // leave behind, but keep paragraph breaks readable.
    return text.replace(/ /g, " ").replace(/[ \t]+\n/g, "\n").trim();
  } catch {
    return html.replace(/<[^>]+>/g, "").trim();
  }
}

function ImagePreview({
  entry,
  accessToken,
}: {
  entry: ClipboardEntry;
  accessToken: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const u = await getClipboardDownloadUrl(accessToken, entry.id);
        if (alive) setUrl(u);
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [entry.id, accessToken]);

  if (err) return <p className="text-[11px] text-rose-600 dark:text-rose-400">image: {err}</p>;
  if (!url)
    return (
      <p className="text-[11px] italic text-slate-400 dark:text-slate-500">Loading preview…</p>
    );
  return (
    <img
      src={url}
      alt="Clipboard image"
      className="max-h-32 max-w-full rounded border border-slate-200 object-contain dark:border-slate-700"
    />
  );
}
